/**
 * **Back to the sign-in that was waiting** (F548). An app sends a person to Sign in with Autho's consent screen,
 * `/sign-in-with-autho/<uid>`; if they are not signed in to Autho there, they sign in first, and every way in (an email
 * link, Google, an eID) comes back to the site's root. So the consent screen's address is remembered while the sign-in
 * screen shows, and taken once signed in, while the sign-in it names can still be answered: the provider's interaction
 * lives ten minutes (`OIDC_TTL.Interaction`).
 *
 * Only that one page, by a strict pattern: never any other address, so nothing here can send a browser elsewhere. It
 * is kept in `localStorage`, which an email link opened in another tab of the same browser still reads; the provider's
 * own cookie is per browser too, so another browser could not finish the sign-in anyway.
 */
const KEY = 'autho.returnTo';

/** How long a remembered sign-in can still be answered: the provider's interaction, ten minutes. */
export const RETURN_WITHIN_MS = 10 * 60_000;

/** The one address a sign-in comes back to: a consent screen, its uid as the provider makes them. */
export const RETURNABLE = /^\/sign-in-with-autho\/[A-Za-z0-9_-]{1,128}$/;

type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

const store = (): Store | null => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

/** Remembers `path` when it is a consent screen; any other address is not remembered, and forgets nothing. */
export function rememberReturn(path: string, now: number, to: Store | null = store()): void {
  if (to === null || !RETURNABLE.test(path)) return;
  to.setItem(KEY, JSON.stringify({ path, at: now }));
}

/** The consent screen to go back to, once: taken and forgotten, and only while its sign-in can still be answered. */
export function takeReturn(now: number, from: Store | null = store()): string | null {
  if (from === null) return null;
  const raw = from.getItem(KEY);
  if (raw === null) return null;
  from.removeItem(KEY);
  try {
    const { path, at } = JSON.parse(raw) as { path?: unknown; at?: unknown };
    if (typeof path !== 'string' || !RETURNABLE.test(path)) return null;
    if (typeof at !== 'number' || now < at || now - at > RETURN_WITHIN_MS) return null;
    return path;
  } catch {
    return null;
  }
}

/** Whether a consent screen is waiting to be gone back to, without taking it (row 14 defers to it). */
export function pendingReturn(now: number, from: Store | null = store()): boolean {
  if (from === null) return false;
  try {
    const raw = from.getItem(KEY);
    if (raw === null) return false;
    const { path, at } = JSON.parse(raw) as { path?: unknown; at?: unknown };
    return (
      typeof path === 'string' &&
      RETURNABLE.test(path) &&
      typeof at === 'number' &&
      now >= at &&
      now - at <= RETURN_WITHIN_MS
    );
  } catch {
    return false;
  }
}
